import { useState, useEffect, useCallback } from 'react';
import { getAdminCommissionPayouts } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const typeColors = {
  fastrack: '#66aaff',
  generation: '#ffaa44',
  performance: '#66ff99',
  reward: '#cc66ff',
};

const AdminCommissionPayouts = () => {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState(null);
  const [slot, setSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getAdminCommissionPayouts({
      type: type !== 'all' ? type : undefined,
      search: search || undefined,
      slot: slot || undefined,
      page,
      limit: 20,
    })
      .then((res) => {
        setPayouts(res.data.payouts || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
        if (res.data.totals) setTotals(res.data.totals);
        if (res.data.availableSlots) setAvailableSlots(res.data.availableSlots);
      })
      .catch((err) => {
        console.error('Error fetching commission payouts:', err);
        setPayouts([]);
      })
      .finally(() => setLoading(false));
  }, [type, search, slot, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const getTypeLabel = (t) => {
    const labels = { fastrack: 'Global Fastrack', generation: 'Generation Bonus', performance: 'Performance Bonus', reward: 'Milestone Reward' };
    return labels[t] || t;
  };

  const getSlotName = (p) => {
    if (!p.description) return null;
    if (p.type === 'fastrack') {
      // "Fastrack - Slot-01"
      const match = p.description.match(/^Fastrack\s*-\s*(.+)$/i);
      return match ? match[1] : null;
    }
    if (p.type === 'generation') {
      // "Level 1 Bonus - @username - Slot-01"
      const parts = p.description.split(' - ');
      return parts.length >= 3 ? parts[parts.length - 1] : null;
    }
    return null;
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Commission Payouts</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 14 }}>{total} total payouts</div>

      {/* Totals chips */}
      {totals && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          {/* Grand total first, highlighted */}
          <div style={{ background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.35)', borderRadius: 10, padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: '#888', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Paid Out</span>
            <span style={{ color: cyan, fontSize: 17, fontWeight: 800, fontFamily: 'monospace' }}>${totals.grand.toFixed(2)}</span>
          </div>
          {[
            { key: 'fastrack', label: 'Fastrack' },
            { key: 'generation', label: 'Generation' },
            { key: 'performance', label: 'Performance' },
            { key: 'reward', label: 'Reward' },
          ].map(({ key, label }) => (
            <div
              key={key}
              style={{ background: `${typeColors[key]}10`, border: `1px solid ${typeColors[key]}35`, borderRadius: 10, padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <span style={{ color: '#666', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
              <span style={{ color: typeColors[key], fontSize: 15, fontWeight: 700, fontFamily: 'monospace' }}>${totals[key].toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}



      {/* Filters & Search */}
      <div style={{ marginBottom: 20 }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by user name, email, or code..."
            style={{ flex: 1, minWidth: 200, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
          />
          <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Search
          </button>
          {search && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Clear
            </button>
          )}
        </form>

        {/* Type Filter */}
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Commission Type</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['all', 'fastrack', 'generation', 'performance', 'reward'].map((t) => (
              <button
                key={t}
                onClick={() => { setType(t); setPage(1); }}
                style={{
                  background: type === t ? cyan : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  color: type === t ? '#000' : '#888',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t === 'all' ? 'All Types' : getTypeLabel(t)}
              </button>
            ))}
          </div>
        </div>

        {/* Slot Filter */}
        {availableSlots.length > 0 && (
          <div>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Slot</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setSlot(''); setPage(1); }}
                style={{
                  background: !slot ? '#cc66ff' : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  color: !slot ? '#000' : '#888',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                All Slots
              </button>
              {availableSlots.map((s) => (
                <button
                  key={s}
                  onClick={() => { setSlot(s); setPage(1); }}
                  style={{
                    background: slot === s ? '#cc66ff' : 'rgba(204,102,255,0.07)',
                    border: `1px solid ${slot === s ? '#cc66ff' : 'rgba(204,102,255,0.2)'}`,
                    color: slot === s ? '#000' : '#cc66ff',
                    borderRadius: 6,
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {!isMobile ? (
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'User', 'Code', 'Commission Type', 'Slot', 'Amount', 'Date'].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td>
                </tr>
              ) : !payouts || payouts.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No payouts found</td>
                </tr>
              ) : (
                payouts.map((p, i) => {
                  const slotName = getSlotName(p);
                  return (
                    <tr
                      key={p._id}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                      <td style={{ color: '#fff', fontSize: 13, fontWeight: 500, padding: '11px 14px', whiteSpace: 'nowrap' }}>{p.userName}</td>
                      <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace', fontWeight: 600 }}>{p.userCode}</td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ background: `${typeColors[p.type]}20`, color: typeColors[p.type], border: `1px solid ${typeColors[p.type]}40`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                          {getTypeLabel(p.type)}
                        </span>
                      </td>
                      <td style={{ color: '#aaa', fontSize: 12, padding: '11px 14px', whiteSpace: 'nowrap' }}>
                        {slotName || <span style={{ color: '#444' }}>—</span>}
                      </td>
                      <td style={{ color: '#66ff99', fontSize: 12, fontWeight: 600, padding: '11px 14px' }}>${p.amount.toFixed(2)}</td>
                      <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(p.createdAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Mobile Card View */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : !payouts || payouts.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>No payouts found</div>
          ) : (
            payouts.map((p) => {
              const slotName = getSlotName(p);
              return (
                <div key={p._id} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12, gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{p.userName}</div>
                      <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace', fontWeight: 600, marginBottom: slotName ? 2 : 0 }}>{p.userCode}</div>
                      {slotName && <div style={{ color: '#aaa', fontSize: 11 }}>{slotName}</div>}
                    </div>
                    <span style={{ background: `${typeColors[p.type]}20`, color: typeColors[p.type], border: `1px solid ${typeColors[p.type]}40`, borderRadius: 6, padding: '4px 10px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {getTypeLabel(p.type)}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ color: '#555', fontSize: 10 }}>Amount</div>
                      <div style={{ color: '#66ff99', fontSize: 13, fontWeight: 600 }}>${p.amount.toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#555', fontSize: 10 }}>Date</div>
                      <div style={{ color: '#888', fontSize: 11 }}>{fmtIST(p.createdAt)}</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Pagination */}
      {pages && pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCommissionPayouts;
